/**
 * ServiceOrderFormDialog - Criação/edição da Ordem de Serviço (Fase 4 OS)
 *
 * Seções: Serviço (Tipo, Prestador, Categoria, Fase, Prioridade), Cliente e Veículo,
 * Agendamento (fim = início + duração do tipo) e Endereço de prestação (CEP automático
 * ou endereço do cliente). Aviso de OS duplicada (BR-OS-002) bloqueia o salvamento.
 * Todas as regras são repetidas no servidor (create/update_service_order).
 *
 * RELEASE: 2026-10-16 v1.5.0 - Responsável editável na criação: o criador continua
 *            pré-selecionado, mas o campo fica sempre habilitado para escolher outro
 *            usuário interno; a exigência de `can_change_responsible` passa a valer
 *            somente na edição de OS existente (a RPC de criação já valida membership).
 * RELEASE: 2026-10-17 v1.5.0 - Campo opcional "Negócio" (serviços do negócio viram itens da O.S.)
 *          e props `defaultCustomer`/`defaultDealId` para "Criar O.S." a partir do Negócio.
 * RELEASE: 2026-10-16 v1.4.0 - Novo prop opcional `defaultScheduledStart`: pré-preenche
 *            o início do agendamento na criação (ex.: slot clicado na Agenda de OS).
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial.
 * RELEASE: 2026-10-13 v1.x - Campo único de coordenadas do local (colado do Google Maps).
 * RELEASE: 2026-10-09 v1.1.0 - Corrige a rolagem interna com a mesma ScrollArea
 *            dedicada usada no formulário de Tarefas.
 * RELEASE: 2026-10-09 v1.2.0 - Correção definitiva da rolagem: a ScrollArea exige pai com
 *            altura definida (viewport usa h-full); com o modal em altura automática
 *            (max-h-90vh) ela crescia até o conteúdo e cortava os últimos campos. O corpo
 *            passa a usar contêiner nativo overflow-y-auto; cabeçalho/rodapé shrink-0 e
 *            gesto de arrastar para fechar desativado (disputava o toque com a rolagem).
 * RELEASE: 2026-10-09 v1.3.0 - Bolinha colorida em Tipo/Categoria/Fase, bandeira na Prioridade
 *            e campos Ponto de Referência, Telefone p/ contato e Link do Google Maps.
 * RELEASE: 2026-10-09 v1.3.1 - Seção Endereço de Prestação no modelo visual da aba Resumo:
 *            duas colunas (Logradouro/Número, Complemento/Bairro, CEP/Município), separador
 *            antes de Referência/Telefone e Link de Localização com botão para abrir o mapa.
 */
import { formRequiresProvider, statusRequiresProvider, PROVIDER_REQUIRED_MESSAGE } from '@/lib/serviceOrders/providerRequirement';
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  Check,
  ChevronsUpDown,
  ClipboardList,
  ExternalLink,
  FileText,
  Loader2,
  MapPin,
  MapPinned,
  Search,
  UserRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { VehicleSelector } from '@/components/tasks/VehicleSelector';
import { PriorityFlag } from '@/components/tasks/PriorityFlag';
import { ADDRESS_PHONE_RE, GOOGLE_MAPS_URL_RE, formatCoordinates, maskPhone, parseCoordinates, sanitizeAddressContactName } from '@/lib/serviceOrderAddress';
import { useServiceOrderDealOptions, useServiceOrderServiceMutations, useServiceOrderServices } from '@/hooks/useServiceOrderServices';
import { useServiceTypes } from '@/hooks/useServiceTypes';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { useCrmCustomersPaginated, type PaginatedCustomer } from '@/hooks/useCrmCustomersPaginated';
import { fetchAddressByCep } from '@/lib/cep-lookup';
import { formatStockDateTime } from '@/lib/stock-presentation';
import { cn } from '@/lib/utils';
import {
  SERVICE_ORDER_PRIORITIES, computeScheduledEnd, useSaveServiceOrder, useServiceOrderDuplicate,
  useServiceOrderEditData, type ServiceOrderInput,
} from '@/hooks/useServiceOrders';
import { SCHEDULE_CONFLICT_CODE, useProviderScheduleCheck, type ScheduleCheck } from '@/hooks/useProviderSchedule';
import { ServiceOrderScheduleConflictDialog } from './ServiceOrderScheduleConflictDialog';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { useServiceOrderPhasePermissions } from '@/hooks/useServiceOrderPhasePermissions';
import { useAuth } from '@/hooks/useAuth';
import { UserSelector } from '@/components/crm/UserSelector';
import { serviceOrderInitialResponsible } from '@/lib/serviceOrderResponsibility';

const EMPTY_ADDRESS = { cep: '', street: '', number: '', complement: '', district: '', city: '', state: '', reference: '', phone: '', contact_name: '', maps_url: '' };
const EMPTY: ServiceOrderInput = {
  id: null, service_type_id: null, category_id: null, phase_id: null, priority: 'medium',
  provider_person_id: null, customer_id: null, vehicle_id: null, address: EMPTY_ADDRESS, scheduled_start: '', description: '',
};

/** ISO → valor do input datetime-local no fuso do navegador. */
const isoToLocal = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const customerName = (c: PaginatedCustomer) => c.nome || c.nome_fantasia || c.razao_social || 'Cliente';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = nova OS */
  serviceOrderId: string | null;
  /** Chamado só ao CRIAR uma OS nova (abre o modal de sucesso na página) */
  onCreated?: (created: { id: string; number: string }) => void;
  /** Categoria/fase escolhidas explicitamente no Kanban, somente na criação. */
  defaultCategoryId?: string;
  defaultPhaseId?: string;
  /** Início do agendamento pré-preenchido (ex.: slot clicado na Agenda de OS), somente na criação. */
  defaultScheduledStart?: string | null;
  /** Cliente pré-preenchido (ex.: "Criar O.S." no modal do Negócio), somente na criação. */
  defaultCustomer?: { id: string; name: string } | null;
  /** Negócio pré-preenchido, somente na criação. */
  defaultDealId?: string | null;
}

export function ServiceOrderFormDialog({ open, onOpenChange, serviceOrderId, onCreated, defaultCategoryId, defaultPhaseId, defaultScheduledStart, defaultCustomer, defaultDealId }: Props) {
  const { user } = useAuth();
  const [form, setForm] = useState<ServiceOrderInput>(EMPTY);
  const [labels, setLabels] = useState<{ provider: string | null; customer: string | null; vehicle: string | null }>({ provider: null, customer: null, vehicle: null });
  const [selectedCustomer, setSelectedCustomer] = useState<PaginatedCustomer | null>(null);
  const [cepLoading, setCepLoading] = useState(false);

  const { serviceTypes } = useServiceTypes(true);
  const { categories } = useTaskCategories(true, undefined, true);
  const { phases } = useTaskPhases(form.category_id ?? undefined, undefined, true);
  const phasePermissions = useServiceOrderPhasePermissions();
  // Catálogo compartilhado de todas as fases para filtrar categorias sem consulta por opção.
  const { phases: allPhases } = useTaskPhases(undefined, undefined, true);
  const edit = useServiceOrderEditData(open ? serviceOrderId : null);
  // Negócio vinculado (gravado à parte por set_service_order_deal, após salvar a O.S.)
  const [dealId, setDealId] = useState<string | null>(null);
  const linkedServices = useServiceOrderServices(open ? serviceOrderId : null);
  const dealMutations = useServiceOrderServiceMutations(null);
  const save = useSaveServiceOrder();
  const duplicate = useServiceOrderDuplicate(form.vehicle_id, form.service_type_id, serviceOrderId);
  // BR-OS-002 v2 / BR-OS-017: bloqueios calculados pela pré-checagem do servidor
  const guard = duplicate.data;
  const openOrder = guard?.duplicate ?? null;
  const isCreate = !serviceOrderId;
  const kitBlocked = isCreate && !!guard && (guard.uninstall_without_kit
    || (guard.requires_install_approval && (!guard.can_approve_install || !form.install_override || !form.override_reason?.trim())));
  const saveBlocked = !!openOrder || kitBlocked;
  // Fase 7: conflito de horário do prestador (modal de escolha de novo horário)
  const checkSchedule = useProviderScheduleCheck();
  // Coordenadas do local: campo único colado do Google Maps ("lat, lng").
  // Carregado junto com os demais dados em get_service_order_edit_data e gravado
  // na mesma chamada de create/update_service_order (gravação atômica).
  const [coords, setCoords] = useState('');
  const [conflict, setConflict] = useState<ScheduleCheck | null>(null);

  const type = useMemo(() => serviceTypes.find((t) => t.id === form.service_type_id) ?? null, [serviceTypes, form.service_type_id]);
  const end = computeScheduledEnd(form.scheduled_start, type?.duration_minutes);
  const readonly = !!edit.data && ['completed', 'cancelled'].includes(edit.data.status);
  const persistedResponsible = edit.data?.responsible_user_id === user?.id;
  // v1.5.0: na criação o responsável é sempre livre (padrão = criador); a permissão
  // por fase `can_change_responsible` passa a valer somente na edição.
  const canAssign = isCreate || phasePermissions.canDoOnPhase(edit.data?.phase_id, 'can_change_responsible', persistedResponsible);

  // Abrir: limpa (nova) ou carrega os dados da edição
  useEffect(() => {
    if (!open) return;
    if (!serviceOrderId) {
      setForm({ ...EMPTY, responsible_user_id: serviceOrderInitialResponsible(user?.id ?? null, false), category_id: defaultCategoryId ?? null, phase_id: defaultPhaseId ?? null, scheduled_start: isoToLocal(defaultScheduledStart ?? null) });
      setLabels({ provider: null, customer: null, vehicle: null });
      setSelectedCustomer(null);
      setDealId(defaultDealId ?? null);
      if (defaultCustomer) {
        setForm((f) => ({ ...f, customer_id: defaultCustomer.id }));
        setLabels((l) => ({ ...l, customer: defaultCustomer.name }));
      }
    }
  }, [open, serviceOrderId, defaultCategoryId, defaultPhaseId, defaultScheduledStart, user?.id, defaultCustomer, defaultDealId]);
  // Edição: negócio atual
  useEffect(() => { if (open && serviceOrderId) setDealId(linkedServices.data?.deal?.id ?? null); }, [open, serviceOrderId, linkedServices.data?.deal?.id]);
  const dealOptions = useServiceOrderDealOptions(open ? form.customer_id : null);
  useEffect(() => {
    const d = edit.data;
    if (!open || !d) return;
    setForm({
      id: d.id, service_type_id: d.service_type_id, category_id: d.category_id, phase_id: d.phase_id, priority: d.priority,
      provider_person_id: d.provider_person_id, customer_id: d.customer_id, vehicle_id: d.vehicle_id,
      address: { ...EMPTY_ADDRESS, ...Object.fromEntries(Object.entries(d.address ?? {}).map(([k, v]) => [k, v ?? ''])) },
      scheduled_start: isoToLocal(d.scheduled_start), description: d.description ?? '',
      seller_id: d.seller_id ?? null,
      responsible_user_id: serviceOrderInitialResponsible(user?.id ?? null, true, d.responsible_user_id),
    });
    setLabels({ provider: d.provider_name, customer: d.customer_name, vehicle: d.vehicle_label });
    setCoords(formatCoordinates(d.destination_lat, d.destination_lng));
  }, [open, edit.data]);

  const set = <K extends keyof ServiceOrderInput>(k: K, v: ServiceOrderInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k: keyof typeof EMPTY_ADDRESS, v: string) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  // Tipo escolhido: aplica categoria/fase padrão (o usuário pode trocar)
  const chooseType = (id: string | null) => {
    const t = serviceTypes.find((s) => s.id === id);
    setForm((f) => ({
      ...f, service_type_id: id,
      category_id: f.category_id ?? t?.default_category_id ?? null,
      phase_id: f.category_id ? f.phase_id : (t?.default_phase_id ?? null),
    }));
  };

  const lookupCep = async () => {
    setCepLoading(true);
    try {
      const a = await fetchAddressByCep(form.address.cep);
      setForm((f) => ({ ...f, address: { ...f.address, street: a.street, district: a.district, city: a.city, state: a.state } }));
    } catch (e: any) {
      toast.error(e?.message ?? 'Erro ao buscar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  const useCustomerAddress = () => {
    const c = selectedCustomer;
    if (!c) return toast.error('Endereço do cliente indisponível. Selecione o cliente novamente.');
    // Mantém referência/telefone/link: só os campos do endereço do cliente são trocados
    setForm((f) => ({ ...f, address: { ...f.address,
      cep: c.cep ?? '', street: c.logradouro ?? '', number: c.numero ?? '', complement: c.complemento ?? '',
      district: c.bairro ?? '', city: c.cidade ?? '', state: c.estado ?? '',
    } }));
  };

  /** Grava a OS; conflito detectado pelo servidor (corrida) reabre o modal de conflito. */
  const persist = async (input: ServiceOrderInput) => {
    // Inclui a confirmação do conflito: não permitir contornar a fase por outro caminho.
    if (!input.id && !phasePermissions.canDoOnPhase(input.phase_id, 'can_create', input.responsible_user_id === user?.id)) {
      toast.error('Sem permissão para criar OS nesta fase');
      return;
    }
    try {
      const r = await save.mutateAsync({ ...input, coordinates: coords.trim() });
      // Vincula/troca o negócio e copia os serviços (servidor valida cliente e situação)
      const savedId = (r as { id?: string }).id ?? input.id;
      const currentDeal = linkedServices.data?.deal?.id ?? null;
      if (savedId && dealId !== (input.id ? currentDeal : null)) {
        try { await dealMutations.setDeal.mutateAsync({ orderId: savedId, dealId }); }
        catch (e: any) { toast.error(e?.message ?? 'Não foi possível vincular o negócio'); }
      }
      // Nova OS com onCreated: o modal de sucesso substitui o aviso
      if (input.id || !onCreated) toast.success(input.id ? `OS ${r.number} atualizada` : `OS ${r.number} criada`);
      setConflict(null);
      onOpenChange(false);
      if (!input.id) onCreated?.(r);
    } catch (e: any) {
      if (e?.code === SCHEDULE_CONFLICT_CODE && input.provider_person_id && input.service_type_id) {
        const c = await checkSchedule({ providerId: input.provider_person_id, start: input.scheduled_start, serviceTypeId: input.service_type_id, excludeId: input.id });
        setConflict(c);
        return;
      }
      toast.error(e?.message ?? 'Erro ao salvar a OS');
    }
  };

  const submit = async () => {
    // Estado anterior decide o acesso: assumir a OS não pode elevar os próprios poderes.
    if (!isCreate && !phasePermissions.canDoOnPhase(edit.data?.phase_id, 'can_edit', persistedResponsible)) return toast.error('Sem permissão para editar OS nesta fase');
    // v1.5.0: a guarda de troca de responsável vale apenas na edição (na criação o campo é livre)
    if (!isCreate && !canAssign && form.responsible_user_id !== edit.data?.responsible_user_id) return toast.error('Sem permissão para alterar o responsável');
    if (!form.service_type_id) return toast.error('Selecione o tipo de serviço');
    // Prestador opcional; obrigatório só quando há data de início (O.S. Agendada) ou status de execução
    if (!form.provider_person_id && (formRequiresProvider(form.scheduled_start) || statusRequiresProvider(edit.data?.status))) return toast.error(PROVIDER_REQUIRED_MESSAGE);
    if (!form.category_id || !form.phase_id) return toast.error('Selecione a categoria e a fase');
    if (!form.customer_id) return toast.error('Selecione o cliente');
    // BR-OS-020: tipo pode exigir vendedor (também validado no servidor)
    if (type?.requires_seller && !form.seller_id) return toast.error('Este tipo de serviço exige o vendedor');
    if (type?.requires_vehicle && !form.vehicle_id) return toast.error('Este tipo de serviço exige o veículo');
    if (type?.requires_address && (!form.address.street.trim() || !form.address.city.trim())) return toast.error('Informe o endereço de prestação');
    if (form.address.phone.trim() && !ADDRESS_PHONE_RE.test(form.address.phone.trim())) return toast.error('Telefone de contato inválido');
    if (form.address.maps_url.trim() && !GOOGLE_MAPS_URL_RE.test(form.address.maps_url.trim())) return toast.error('Informe um link https do Google Maps');
    if (coords.trim() && !parseCoordinates(coords)) return toast.error('Coordenadas inválidas. Cole no formato -16.756515, -49.325765');
    if (openOrder) return toast.error(`Já existe a OS ${openOrder.number} em aberto para este veículo`);
    if (isCreate && guard?.uninstall_without_kit) return toast.error('Não há kit instalado neste veículo para desinstalar');
    if (kitBlocked) return toast.error('Instalação com kit já instalado exige aprovação e justificativa');
    // BR-OS-004/008: verifica a agenda do prestador antes de gravar
    if (form.scheduled_start && form.provider_person_id) {
      try {
        const c = await checkSchedule({ providerId: form.provider_person_id, start: form.scheduled_start, serviceTypeId: form.service_type_id, excludeId: form.id });
        if (c.conflicts.length > 0) { setConflict(c); return; }
      } catch (e: any) {
        return toast.error(e?.message ?? 'Não foi possível verificar a agenda do prestador');
      }
    }
    await persist(form);
  };

  const typeOptions = serviceTypes.map((t) => ({ value: t.id, label: t.name, hint: `${t.duration_minutes} min`, color: t.color }));
  const categoryOptions = categories.filter(c => !isCreate || allPhases.some(p => p.category_id === c.id && phasePermissions.canDoOnPhase(p.id, 'can_create', form.responsible_user_id === user?.id))).map(c => ({ value: c.id, label: c.name, color: c.color }));
  const phaseOptions = phases.filter(p => isCreate ? phasePermissions.canDoOnPhase(p.id, 'can_create', form.responsible_user_id === user?.id) : p.id === edit.data?.phase_id || phasePermissions.canDoOnPhase(p.id, 'can_change_phase', persistedResponsible)).map(p => ({ value: p.id, label: p.name, color: p.color }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent enableSwipeToClose={false} className="flex h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col gap-0 overflow-hidden p-0 sm:h-auto sm:max-h-[90vh] sm:max-w-5xl">
        {/* Cabeçalho alinhado ao padrão premium do formulário de Tarefas. */}
        <DialogHeader className="flex shrink-0 flex-row items-center gap-3 border-b border-border/50 bg-background/80 px-4 py-4 text-left backdrop-blur-sm sm:px-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20">
            <ClipboardList className="h-5 w-5 text-primary" />
          </div>
          <div className="flex min-w-0 flex-col">
            <DialogTitle className="truncate text-base font-semibold leading-tight">
              {form.id ? 'Editar Ordem de Serviço' : 'Nova Ordem de Serviço'}
            </DialogTitle>
            <DialogDescription className="mt-0.5 truncate text-xs text-muted-foreground">
              {form.id
                ? `Atualize os dados da ${edit.data?.number ?? 'ordem de serviço'}`
                : 'Preencha os campos para criar uma nova ordem de serviço'}
            </DialogDescription>
          </div>
        </DialogHeader>

        {serviceOrderId && edit.isLoading ? (
          <div className="flex flex-1 items-center justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          /* Rolagem nativa: funciona com altura automática do modal (a ScrollArea não). */
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid="os-form-scroll">
          <fieldset disabled={readonly} className="space-y-6 px-4 py-5 sm:px-6">
            {readonly && <p className="text-sm text-muted-foreground">OS concluída ou cancelada: somente leitura.</p>}

            <section>
              <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
                <ClipboardList className="h-4 w-4 text-primary" />
                Serviço
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Responsabilidade interna reutiliza o seletor e o cache de membros ativos. */}
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Responsável pela OS</Label>
                  <UserSelector value={form.responsible_user_id} onSelect={id => set('responsible_user_id', id)} disabled={readonly || !canAssign} allowClear={!isCreate} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tipo de Serviço *</Label>
                  <SearchableSelect value={form.service_type_id} onChange={chooseType} options={typeOptions} placeholder="Selecione o tipo" />
                </div>
                <div className="space-y-1.5">
                  {/* Obrigatório apenas ao agendar (data de início) — regra em providerRequirement */}
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Prestador de Serviço{formRequiresProvider(form.scheduled_start) ? ' *' : ''}</Label>
                  <PersonSelector role="service_provider" value={form.provider_person_id} selectedLabel={labels.provider}
                    onSelect={(id) => set('provider_person_id', id)} placeholder="Selecione o prestador" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Fase *</Label>
                  <SearchableSelect value={form.phase_id} options={phaseOptions} placeholder="Selecione a fase"
                    disabled={!form.category_id} onChange={(id) => set('phase_id', id)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Categoria *</Label>
                  <SearchableSelect value={form.category_id} options={categoryOptions} placeholder="Selecione a categoria"
                    onChange={(id) => setForm((f) => ({ ...f, category_id: id, phase_id: null }))} />
                </div>
                {/* BR-OS-020: vendedor vinculado à OS */}
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Vendedor {type?.requires_seller ? '*' : '(opcional)'}</Label>
                  <SellerSelector value={form.seller_id} onSelect={(id) => set('seller_id', id)} placeholder="Selecione o vendedor" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Prioridade</Label>
                  <Select value={form.priority} onValueChange={(v) => set('priority', v as ServiceOrderInput['priority'])}>
                    <SelectTrigger className="h-9 border-border/50"><SelectValue /></SelectTrigger>
                    {/* Bandeiras iguais às prioridades de Tarefas */}
                    <SelectContent>{SERVICE_ORDER_PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        <span className="flex items-center gap-2"><PriorityFlag priority={p.value} size={14} />{p.label}</span>
                      </SelectItem>
                    ))}</SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            <Separator />

            <section>
              <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
                <UserRound className="h-4 w-4 text-primary" />
                Cliente e Veículo
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Cliente *</Label>
                  <CustomerPicker value={form.customer_id} label={labels.customer} onSelect={(c) => {
                    setSelectedCustomer(c);
                    setLabels((l) => ({ ...l, customer: c ? customerName(c) : null, vehicle: null }));
                    setForm((f) => ({ ...f, customer_id: c?.id ?? null, vehicle_id: null }));
                    setDealId(null);
                  }} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Veículo {type?.requires_vehicle ? '*' : '(opcional)'}</Label>
                  <VehicleSelector value={form.vehicle_id} fallbackLabel={labels.vehicle} scopeCustomerId={form.customer_id}
                    disabled={!form.customer_id} onSelect={(id) => set('vehicle_id', id)} />
                </div>
              </div>
              {/* Serviços do negócio vão para a aba Itens da O.S. (BR-OS-046) */}
              <div className="mt-3 space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Negócio (opcional)</Label>
                <SearchableSelect value={dealId} onChange={setDealId} noneLabel="Sem negócio" disabled={!form.customer_id || readonly}
                  isLoading={dealOptions.isLoading} selectedLabel={linkedServices.data?.deal ? `${linkedServices.data.deal.code} · ${linkedServices.data.deal.title}` : null}
                  options={(dealOptions.data ?? []).map((d) => ({ value: d.id, label: `${d.code} · ${d.title}`, hint: `${d.services} serviço(s)` }))} />
              </div>
              {openOrder && (
                <div className="flex gap-3 border-l-4 border-destructive py-2 pl-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <div className="text-sm text-foreground">
                    <p className="font-medium">OS em aberto para este veículo</p>
                    <p className="text-muted-foreground">Já existe a OS <strong className="text-foreground">{openOrder.number}</strong> ("{openOrder.service_type_name}") ainda não concluída. Conclua ou cancele antes de abrir outra.</p>
                  </div>
                </div>
              )}
              {/* BR-OS-017: Desinstalação exige kit instalado */}
              {isCreate && !openOrder && guard?.uninstall_without_kit && (
                <div className="flex gap-3 border-l-4 border-destructive py-2 pl-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <div className="text-sm text-foreground">
                    <p className="font-medium">Veículo sem kit instalado</p>
                    <p className="text-muted-foreground">Este tipo de serviço é de desinstalação, mas não há kit instalado no veículo.</p>
                  </div>
                </div>
              )}
              {/* BR-OS-017: Instalação com kit já instalado exige aprovação */}
              {isCreate && !openOrder && guard?.requires_install_approval && (
                <div className="flex gap-3 border-l-4 border-warning py-2 pl-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                  <div className="min-w-0 flex-1 space-y-2 text-sm text-foreground">
                    <p className="font-medium">Veículo já possui kit instalado</p>
                    <ul className="text-muted-foreground">
                      {guard.installed_kits.map((k) => (
                        <li key={k.id}>Kit <strong className="text-foreground">{k.label}</strong>{k.tracker ? ` · Rastreador ${k.tracker}` : ''}{k.chip ? ` · Chip ${k.chip}` : ''}</li>
                      ))}
                    </ul>
                    {guard.can_approve_install ? (
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={!!form.install_override}
                            onChange={(e) => setForm((f) => ({ ...f, install_override: e.target.checked }))} />
                          Aprovar criação mesmo com kit instalado
                        </label>
                        {form.install_override && (
                          <Textarea rows={2} maxLength={500} placeholder="Justificativa da aprovação (obrigatória)"
                            value={form.override_reason ?? ''}
                            onChange={(e) => setForm((f) => ({ ...f, override_reason: e.target.value.replace(/[<>]/g, '') }))} />
                        )}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">Requer aprovação de um responsável com permissão para aprovar instalação com kit já instalado.</p>
                    )}
                  </div>
                </div>
              )}
            </section>

            <Separator />

            <section>
              <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
                <CalendarClock className="h-4 w-4 text-primary" />
                Agendamento
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Início</Label>
                  <Input className="h-9 border-border/50" type="datetime-local" value={form.scheduled_start} onChange={(e) => set('scheduled_start', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Fim previsto</Label>
                  <Input className="h-9 border-border/50 bg-muted" readOnly value={end ? formatStockDateTime(end.toISOString()) : '—'} />
                  {type && <p className="text-xs text-muted-foreground">Duração do tipo: {type.duration_minutes} min. Sem início, a OS fica Aberta.</p>}
                </div>
              </div>
            </section>

            <Separator />

            <section>
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <MapPinned className="h-4 w-4 text-primary" />
                  Endereço de Prestação do Serviço {type?.requires_address ? '*' : ''}
                </h3>
                <Button type="button" variant="outline" size="sm" onClick={useCustomerAddress} disabled={!form.customer_id}>
                  <MapPin className="mr-1.5 h-4 w-4" />Usar endereço do cliente
                </Button>
              </div>
              {/* Modelo visual da aba Resumo: duas colunas (campo longo à esquerda), separador
                  antes de Referência/Telefone e Link de Localização com atalho para abrir o mapa. */}
              <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Logradouro</Label>
                  <Input className="h-9 border-border/50" maxLength={200} value={form.address.street} onChange={(e) => setAddr('street', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Número</Label>
                  <Input className="h-9 border-border/50" maxLength={20} value={form.address.number} onChange={(e) => setAddr('number', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Complemento</Label>
                  <Input className="h-9 border-border/50" maxLength={120} value={form.address.complement} onChange={(e) => setAddr('complement', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Bairro</Label>
                  <Input className="h-9 border-border/50" maxLength={120} value={form.address.district} onChange={(e) => setAddr('district', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">CEP</Label>
                  <div className="flex gap-2">
                    <Input className="h-9 border-border/50" value={form.address.cep} maxLength={9} onChange={(e) => setAddr('cep', e.target.value)} onBlur={() => form.address.cep.replace(/\D/g, '').length === 8 && lookupCep()} />
                    <Button type="button" variant="outline" size="icon" onClick={lookupCep} disabled={cepLoading} aria-label="Buscar CEP">
                      {cepLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {/* Município exibido como no Resumo: cidade + UF juntos */}
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Município</Label>
                  <div className="flex gap-2">
                    <Input className="h-9 border-border/50" placeholder="Cidade" maxLength={120} value={form.address.city} onChange={(e) => setAddr('city', e.target.value)} />
                    <Input className="h-9 w-16 shrink-0 border-border/50 text-center" placeholder="UF" maxLength={2} value={form.address.state} onChange={(e) => setAddr('state', e.target.value.toUpperCase())} />
                  </div>
                </div>
                <Separator className="sm:col-span-2" />
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ponto de Referência</Label>
                  <Input className="h-9 border-border/50" maxLength={200} value={form.address.reference} onChange={(e) => setAddr('reference', e.target.value.replace(/[<>]/g, ''))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Telefone p/ Contato no Endereço</Label>
                  <Input className="h-9 border-border/50" inputMode="tel" maxLength={20} placeholder="(00) 00000-0000" value={form.address.phone} onChange={(e) => setAddr('phone', maskPhone(e.target.value))} />
                </div>
                {/* Nome da pessoa de contato no local (ex.: quem recebe o prestador) */}
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nome do Contato no Endereço</Label>
                  <Input className="h-9 border-border/50" maxLength={120} placeholder="Ex.: Maria Silva" value={form.address.contact_name} onChange={(e) => setAddr('contact_name', sanitizeAddressContactName(e.target.value))} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Link Localização (Google Maps)</Label>
                  <div className="flex gap-2">
                    <Input className="h-9 border-border/50" type="url" inputMode="url" maxLength={500} placeholder="https://maps.app.goo.gl/..." value={form.address.maps_url} onChange={(e) => setAddr('maps_url', e.target.value.replace(/[\s<>"']/g, ''))} />
                    {/* Abre o link em nova aba quando é um URL válido do Google Maps */}
                    <Button type="button" variant="outline" size="icon" aria-label="Abrir no mapa" disabled={!GOOGLE_MAPS_URL_RE.test(form.address.maps_url)}
                      onClick={() => window.open(form.address.maps_url, '_blank', 'noopener,noreferrer')}>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Coordenadas do local (copiar do Google Maps)</Label>
                  <div className="flex gap-2">
                    <Input className="h-9 border-border/50" inputMode="decimal" maxLength={60} placeholder="-16.756515700478197, -49.325765008648915"
                      value={coords} onChange={(e) => setCoords(e.target.value.replace(/[^0-9.,\-\s]/g, ''))}
                      aria-invalid={!!coords.trim() && !parseCoordinates(coords)} />
                    {/* Confere o ponto colado no Google Maps, sem chave de API */}
                    <Button type="button" variant="outline" size="icon" aria-label="Conferir no Google Maps" disabled={!parseCoordinates(coords)}
                      onClick={() => { const c = parseCoordinates(coords); if (c) window.open(`https://www.google.com/maps/search/?api=1&query=${c.lat},${c.lng}`, '_blank', 'noopener,noreferrer'); }}>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Usadas para confirmar a chegada do prestador ao local.</p>
                </div>
              </div>
            </section>

            <Separator />

            <section>
              <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
                <FileText className="h-4 w-4 text-primary" />
                Descrição
              </h3>
              <Textarea className="min-h-28 border-border/50" rows={4} maxLength={5000} value={form.description} onChange={(e) => set('description', e.target.value.replace(/[<>]/g, ''))} />
            </section>
          </fieldset>
          </div>
        )}

        {/* Rodapé fixo: permanece acessível mesmo com formulários longos. */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border/50 bg-muted/20 px-4 py-4 sm:px-6">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={save.isPending} className="text-muted-foreground hover:text-foreground">Cancelar</Button>
          {!readonly && (
            <Button size="sm" onClick={submit} disabled={save.isPending || saveBlocked || (isCreate ? !phasePermissions.canDoOnPhase(form.phase_id, 'can_create', form.responsible_user_id === user?.id) : !phasePermissions.canDoOnPhase(edit.data?.phase_id, 'can_edit', persistedResponsible))} className="min-w-[140px] gap-2">
              {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardList className="h-3.5 w-3.5" />}
              {form.id ? 'Salvar' : 'Criar OS'}
            </Button>
          )}
        </div>
        {/* BR-OS-008: escolha de novo horário quando o prestador está ocupado */}
        <ServiceOrderScheduleConflictDialog open={!!conflict} check={conflict} providerName={labels.provider}
          onCancel={() => setConflict(null)}
          onRecheck={(start) => {
            if (!form.provider_person_id || !form.service_type_id) throw new Error('Prestador e tipo de serviço são obrigatórios');
            return checkSchedule({ providerId: form.provider_person_id, start, serviceTypeId: form.service_type_id, excludeId: form.id });
          }}
          onConfirm={(start) => { const next = { ...form, scheduled_start: start }; setForm(next); void persist(next); }} />
      </DialogContent>
    </Dialog>
  );
}

/** Busca de cliente no servidor (reaproveita get_customers_paginated). */
export function CustomerPicker({ value, label, onSelect }: { value: string | null; label: string | null; onSelect: (c: PaginatedCustomer | null) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);
  const { customers, isLoading } = useCrmCustomersPaginated({ search: debounced || null } as any, { page: 1, pageSize: 20 });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal">
          <span className={cn('truncate', !value && 'text-muted-foreground')}>{value ? (label ?? 'Cliente selecionado') : 'Selecione o cliente'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar por nome ou documento..." value={search} onValueChange={(v) => setSearch(v.slice(0, 100))} />
          <CommandList>
            <CommandEmpty>{isLoading ? 'Carregando...' : 'Nenhum cliente encontrado'}</CommandEmpty>
            <CommandGroup>
              {customers.map((c) => (
                <CommandItem key={c.id} value={c.id} onSelect={() => { onSelect(c); setOpen(false); }}>
                  <Check className={cn('mr-2 h-4 w-4', value === c.id ? 'opacity-100' : 'opacity-0')} />
                  <div className="min-w-0">
                    <p className="truncate">{customerName(c)}</p>
                    <p className="truncate text-xs text-muted-foreground">{c.cpf || c.cnpj || ''}</p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
